import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { useMediaQuery, Box, useTheme, Paper, Skeleton, TextField, Typography } from "@mui/material";
import {
  Button,
  Checkbox,
  ChoiceToggle,
  CountrySelect,
  FieldWrapper,
  LinkTerminosPrivacidad,
  PageHeader,
  SectionHeader,
} from "@components/ui_v2";
import { CardResumenCotizacion, SliderCotizador } from "@components/ui_v2/cotizadoresComponents";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { TaxSystemConfigResponse } from "@interfaces/index";
import { TaxSystemCodes } from "@interfaces/store";
import { buildTermOptions, getClosestTerm, formatCurrencyShort, formatCurrencyWhole } from "@helpers/funciones";
import { PATHS_TOFU_V2 } from "@helpers/constantes";
import { TAX_SYSTEMS_CODES } from "@helpers/taxSystemsCodes";
import { PHONE_NUMBER } from "@utils/expreciones";

interface FormValues {
  amount: number;
  term: number;
  monthly_rate: number;
  trade_name: string;
  country_code: string;
  phone: string;
  terminos: boolean;
}

/**
 * El régimen ya no se elige aquí: se cotiza con la config de PM y el registro elige el tax system según el RFC
 * (12 = PM, 13 = PFAE) con estos códigos, que viajan a `CrearCuenta` en `tax_system_codes`.
 */
const TAX_SYSTEM_CODES: TaxSystemCodes = { PM: TAX_SYSTEMS_CODES.FLUX_PM, PFAE: TAX_SYSTEMS_CODES.FLUX_PFAE };

/** Opción "Otro plazo" del grupo de plazos y su tope, como en el prototipo ("Hasta 360 días."). */
const OTRO_PLAZO = "otro";
const MAX_DIAS_OTRO_PLAZO = 360;

/** Tasa mensual que elige el usuario (`Tasa mensual` del prototipo): de 1.5 % a 4 %, 2 % por default. */
const TASA_MENSUAL = { min: 1.5, max: 4, step: 0.1, default: 2 };

/** Simulación de la hoja de cálculo de Flux: reserva de garantía (aforo) del 10 % y comisión del 1 % + IVA. */
const AFORO = 0.1;
const COMISION = 0.01;
const IVA = 1.16;

const EMPTY_CONFIG: TaxSystemConfigResponse = {
  id: "",
  code: "",
  product_id: "",
  minAmount: 0,
  maxAmount: 0,
  stepAmount: 0,
  minTerm: 0,
  maxTerm: 0,
  stepTerm: 0,
  defaultAmount: 0,
  defaultTerm: 0,
  tax_system_type: "",
};

const validationSchema = yup.object().shape({
  trade_name: yup.string().trim().required("Campo requerido"),
  country_code: yup.string().required("Campo requerido"),
  phone: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
  terminos: yup.boolean().oneOf([true], "Para continuar, acepta los términos y condiciones."),
});

/**
 * Cotizador de factoraje de Flux (`Cotizador` del prototipo con `factoraje` y `contacto`): monto de la línea,
 * plazo de las facturas en días (los de la config, más "Otro plazo"), tasa mensual, datos de contacto y la
 * aceptación de términos (Flux los acepta aquí y no en `CrearCuenta`).
 */
const CotizadorFlux = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>(EMPTY_CONFIG);
  // "Otro plazo": el usuario escribe los días en lugar de elegir una de las opciones.
  const [otroPlazo, setOtroPlazo] = useState(false);
  const [plazoLibre, setPlazoLibre] = useState("");

  const formik = useFormik<FormValues>({
    initialValues: {
      amount: 0,
      term: 0,
      monthly_rate: TASA_MENSUAL.default,
      trade_name: "",
      country_code: "+52",
      phone: "",
      terminos: false,
    },
    validationSchema,
    onSubmit: (values) => {
      if (!cotizadorConfig.product_id || !values.amount || !values.term) {
        return;
      }
      const sourceCode = sessionStorage.getItem("source_code");

      let urlPortal = `${PATHS_TOFU_V2.crearCuenta.path}?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${values.amount}&loan_term=${values.term}&tax_system_type=${cotizadorConfig.tax_system_type}`;
      urlPortal += `&tax_system_codes=${encodeURIComponent(JSON.stringify(TAX_SYSTEM_CODES))}`;
      // Contacto: prellena el registro (`RegistroDatosPersonales`). `encodeURIComponent` porque la lada trae `+`.
      urlPortal += `&trade_name=${encodeURIComponent(values.trade_name.trim())}&code_phone_number=${encodeURIComponent(values.country_code)}&phone_number=${values.phone}`;
      // Tasa anual (%): el registro la manda en `application_condition.interest_rate`.
      urlPortal += `&interest_rate=${Number((values.monthly_rate * 12).toFixed(2))}`;
      if (sourceCode) {
        urlPortal += `&source_code=${sourceCode}`;
      }
      navigate(urlPortal);
    },
  });

  const { values, setFieldValue } = formik;

  const termOptions = useMemo(
    () => buildTermOptions(cotizadorConfig.minTerm, cotizadorConfig.maxTerm, cotizadorConfig.stepTerm),
    [cotizadorConfig.minTerm, cotizadorConfig.maxTerm, cotizadorConfig.stepTerm],
  );

  // Simulación sobre facturas por el 100 % de la línea: se retiene el aforo y sobre el importe restante se cobran
  // intereses diarios (tasa anual / 360 por los días del plazo) y la comisión con IVA.
  const simulacion = useMemo(() => {
    const aforo = values.amount * AFORO;
    const importe = values.amount - aforo;
    const intereses = ((importe * ((values.monthly_rate * 12) / 100)) / 360) * values.term;
    const comision = importe * COMISION * IVA;
    return { aforo, intereses, comision, neto: importe - intereses - comision };
  }, [values.amount, values.term, values.monthly_rate]);

  const getInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const responsePM = await getTaxSystemConfig(TAX_SYSTEM_CODES.PM as string).unwrap();
      setCotizadorConfig(responsePM);
    } catch (error) {
      // Mismo alcance que el legacy: sin config la pantalla queda en ceros, sin modal de error.
      Sentry.captureException(error, { extra: { component: "CotizadorFlux-initial_fetch" } });
    } finally {
      setIsLoading(false);
    }
  }, [getTaxSystemConfig]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  // Al llegar la config se toman sus defaults.
  useEffect(() => {
    const { minTerm, maxTerm, stepTerm, defaultAmount, defaultTerm } = cotizadorConfig;

    if (minTerm < maxTerm && stepTerm > 0) {
      setFieldValue("amount", defaultAmount);
      setFieldValue("term", getClosestTerm(buildTermOptions(minTerm, maxTerm, stepTerm), defaultTerm));
    }
  }, [cotizadorConfig, setFieldValue]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  const handlePlazoLibre = (texto: string) => {
    const limpio = texto.replace(/[^0-9]/g, "").slice(0, 3);
    setPlazoLibre(limpio);
    const dias = Math.min(MAX_DIAS_OTRO_PLAZO, Math.max(1, Number(limpio) || 0));
    if (limpio) setFieldValue("term", dias);
  };

  const desglose = [
    { label: "Valor de las facturas", value: formatCurrencyWhole(values.amount) },
    { label: `Reserva de garantía (${AFORO * 100} %)`, value: `−${formatCurrencyWhole(simulacion.aforo)}` },
    { label: `Intereses (${values.term || 0} días)`, value: `−${formatCurrencyWhole(simulacion.intereses)}` },
    {
      label: `Comisión por operación (${COMISION * 100} % + IVA)`,
      value: `−${formatCurrencyWhole(simulacion.comision)}`,
    },
  ];
  const bloqueado = isLoading || formik.isSubmitting;

  return (
    <Box>
      <PageHeader
        variant="cotizador"
        eyebrow="Cotización"
        title="Solicita tu línea de factoraje"
        subtitle="Línea revolvente para anticipar el cobro de tus facturas. Ajusta el monto de la línea y el plazo de las facturas."
      />

      <Paper variant="lendia_paper">
        <SectionHeader
          variant="cotizador"
          label="Monto de la línea"
          meta={`${formatCurrencyShort(cotizadorConfig.minAmount, true)} – ${formatCurrencyShort(cotizadorConfig.maxAmount, true)} MXN`}
          first
        />
        <SliderCotizador
          value={values.amount}
          onChange={(value) => setFieldValue("amount", value)}
          min={cotizadorConfig.minAmount}
          max={cotizadorConfig.maxAmount}
          step={cotizadorConfig.stepAmount}
          valueText={formatCurrencyShort(values.amount, true)}
          unit="MXN"
          minLabel={formatCurrencyShort(cotizadorConfig.minAmount, true)}
          maxLabel={formatCurrencyShort(cotizadorConfig.maxAmount, true)}
          loading={isLoading}
          testId="cotizador-flux-monto-slider"
        />

        <SectionHeader
          variant="cotizador"
          label="Plazo de las facturas"
          meta="Días que tus clientes tardan en pagarte"
        />
        {!isLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* "Otro plazo" es una opción más del grupo; al elegirla se escriben los días. */}
            <ChoiceToggle
              variant="cotizador"
              value={otroPlazo ? OTRO_PLAZO : values.term}
              onChange={(value) => {
                if (value === OTRO_PLAZO) {
                  setOtroPlazo(true);
                  return;
                }
                setOtroPlazo(false);
                setPlazoLibre("");
                setFieldValue("term", value);
              }}
              options={[
                ...termOptions.map((term) => ({ value: term, label: `${term} días` })),
                { value: OTRO_PLAZO, label: "Otro plazo" },
              ]}
              testId="cotizador-flux-plazo"
            />
            {otroPlazo && (
              <FieldWrapper label="¿A cuántos días?" hint={`Hasta ${MAX_DIAS_OTRO_PLAZO} días.`}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <TextField
                    autoComplete="off"
                    size="lendia_input"
                    placeholder="ej. 45"
                    value={plazoLibre}
                    onChange={(e) => handlePlazoLibre(e.target.value)}
                    disabled={bloqueado}
                    sx={{ width: 125 }}
                    inputProps={{ inputMode: "numeric", "data-testid": "cotizador-flux-otro-plazo-input" }}
                  />
                  <Typography variant="body2" color="tokens.textSecondary">
                    días
                  </Typography>
                </Box>
              </FieldWrapper>
            )}
          </Box>
        ) : (
          <Skeleton variant="rounded" animation="wave" height={50} />
        )}

        <SectionHeader
          variant="cotizador"
          label="Tasa mensual"
          meta={`${TASA_MENSUAL.min} % – ${TASA_MENSUAL.max} % mensual`}
        />
        <SliderCotizador
          value={values.monthly_rate}
          onChange={(value) => setFieldValue("monthly_rate", value)}
          min={TASA_MENSUAL.min}
          max={TASA_MENSUAL.max}
          step={TASA_MENSUAL.step}
          valueText={values.monthly_rate.toFixed(1)}
          unit="% mensual"
          minLabel={`${TASA_MENSUAL.min} %`}
          maxLabel={`${TASA_MENSUAL.max} %`}
          loading={isLoading}
          testId="cotizador-flux-tasa-slider"
        />

        <SectionHeader variant="cotizador" label="Tus datos de contacto" meta="Para enviarte tu oferta" />
        <Box
          sx={{ display: "grid", gridTemplateColumns: isMobileOrTablet ? "1fr" : "repeat(2, 1fr)", gap: "12px 14px" }}
        >
          <FieldWrapper
            label="Nombre comercial"
            required
            hint="Como te conocen tus clientes; puede ser distinto a tu razón social."
            error={(formik.touched.trade_name && formik.errors.trade_name) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="trade_name"
              placeholder="Textiles del Norte"
              value={values.trade_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={bloqueado}
              required
              inputProps={{ "aria-required": true, "data-testid": "cotizador-flux-nombre-comercial-input" }}
            />
          </FieldWrapper>
          <FieldWrapper
            label="Teléfono celular"
            required
            hint="A este número te contactamos y enviamos tu oferta por WhatsApp."
            error={(formik.touched.phone && formik.errors.phone) || ""}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <CountrySelect
                value={values.country_code}
                onChange={(value) => setFieldValue("country_code", value)}
                disabled={bloqueado}
              />
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="phone"
                placeholder="55 1234 5678"
                value={values.phone}
                onChange={(e) => setFieldValue("phone", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                onBlur={formik.handleBlur}
                disabled={bloqueado}
                required
                inputProps={{ "aria-required": true, "data-testid": "cotizador-flux-telefono-input" }}
              />
            </Box>
          </FieldWrapper>
        </Box>
      </Paper>

      <CardResumenCotizacion
        intro={{
          eyebrow: "Simulación de tu operación",
          text: "Ejemplo calculado sobre facturas por el 100 % del monto de tu línea.",
        }}
        label="Importe neto estimado a recibir"
        amount={formatCurrencyWhole(simulacion.neto)}
        caption="MXN"
        disclaimer="Estimación con fines informativos. Termina tu solicitud hoy mismo y recibe tu oferta en menos de 48 horas hábiles, a partir de que tu solicitud esté completa. La oferta final está sujeta a aprobación y al análisis de las facturas y sus deudores."
        footer={
          // Flux acepta los términos aquí, en la primera pantalla, y no en `CrearCuenta`.
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Checkbox
              variant="brand"
              checked={values.terminos}
              onChange={(checked) => setFieldValue("terminos", checked)}
              disabled={bloqueado}
              label={
                <Typography variant="body2" fontWeight={500} color="primary.dark" component="span">
                  Acepto los{" "}
                  <LinkTerminosPrivacidad
                    termsLabel="términos y condiciones"
                    connector="y la"
                    privacyLabel="política de privacidad"
                  />
                </Typography>
              }
              testId="cotizador-flux-terminos-checkbox"
            />
            {formik.touched.terminos && formik.errors.terminos && (
              <Typography variant="body2" color="error.main" role="alert">
                {formik.errors.terminos}
              </Typography>
            )}
          </Box>
        }
        actions={
          <Button
            tone="hero"
            withArrow
            onClick={formik.submitForm}
            disabled={bloqueado}
            data-testid="cotizador-flux-continue-button"
          >
            Solicitar financiamiento
          </Button>
        }
      >
        {/* Desglose del importe neto (`Row` del prototipo). */}
        <Box sx={{ mt: 1.75, display: "flex", flexDirection: "column", gap: "6px", maxWidth: 380 }}>
          {desglose.map((item) => (
            <Box key={item.label} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <Typography variant="body2" color="tokens.textTertiary">
                {item.label}
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardResumenCotizacion>
    </Box>
  );
};
export default CotizadorFlux;
